import { create } from 'zustand';
import type { Asset, AssetGroup } from '@scio3/shared-types';

/**
 * Split-screen smallapp two-step flow state. Replaces the scio1 iframe
 * `OPEN_APP` postMessage path for `appType === 'splitScreen'`.
 *
 * Phase 2 mounts only the Templates dialog; the Zones state shape is
 * already defined here so Phase 3's `SplitScreenZonesDialog` can drop
 * straight in without a store rewrite.
 */
export type SplitScreenDialogType = 'zones' | null;

export interface SplitScreenZone {
  id?: string;
  name?: string;
  top?: number;
  left?: number;
  width?: number;
  height?: number;
  topPixel?: number;
  leftPixel?: number;
  widthPixel?: number;
  heightPixel?: number;
  currentType?: 'asset' | 'playlist' | 'schedule';
  currentAssetId?: string;
  currentPlaylistId?: string;
  currentScheduleId?: string;
  scale?: 'none' | 'stretch' | 'fit' | 'zoom';
  documentDuration?: number;
  color?: string;
  text?: string;
  usePrimaryZoneTiming?: boolean;
}

export interface SplitScreenAsset extends Partial<Asset> {
  type?: 'splitScreen';
  screenZones?: SplitScreenZone[];
  orientation?: 'landscape' | 'portrait';
  resolution?: string;
  resolutionWidth?: number;
  resolutionHeight?: number;
  unitType?: 'percent' | 'pixel';
  primaryZone?: string | null;
  respectPrimaryZoneTiming?: boolean | null;
  audioZone?: number | null;
  backGroundMusicZone?: number | null;
  template?: string;
}

interface SplitScreenDialogContext {
  currentPath?: string;
  currentTeamId?: string;
  currentGroup?: AssetGroup | null;
  mode?: 'add' | 'edit';
  asset?: SplitScreenAsset;
}

interface SplitScreenDialogState {
  activeDialog: SplitScreenDialogType;
  dialogContext: SplitScreenDialogContext;

  /** Opened from `useOpenAssetEditor` for an existing splitScreen asset (edit mode). */
  openZones: (args: {
    mode: 'add' | 'edit';
    asset: SplitScreenAsset;
    currentPath?: string;
    currentTeamId?: string;
  }) => void;

  closeDialog: () => void;
}

export const useSplitScreenDialogStore = create<SplitScreenDialogState>((set) => ({
  activeDialog: null,
  dialogContext: {},

  openZones: ({ mode, asset, currentPath, currentTeamId }) =>
    set({
      activeDialog: 'zones',
      dialogContext: { mode, asset, currentPath, currentTeamId },
    }),

  closeDialog: () => set({ activeDialog: null, dialogContext: {} }),
}));
